/**
 * dayRequestPlan — how many `/calendar/events/day` requests a span costs, as
 * pure functions so the count is testable.
 *
 * A contiguous span is asked for in ONE request (`date` = first displayed day,
 * `days` = span length). The server echoes the window it served as `range`
 * (unix seconds of request-timezone midnights, `to` exclusive); anything that
 * does not provably cover the span falls back to one request per day.
 *
 * @module dayRequestPlan
 */
import { fromDisplayMs } from '../../calendar-week/utils/weekTimeMath.js';

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

function dayUtcMs(iso) {
	const m = ISO_DAY.exec(String(iso || ''));
	return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/**
 * True when `dates` are valid ISO days, each exactly one calendar day after the
 * previous one. Pseudo-UTC arithmetic, so DST never skews it.
 *
 * @param {string[]} dates
 */
export function isContiguousSpan(dates) {
	if (!Array.isArray(dates) || dates.length === 0) return false;
	let prev = null;
	for (const iso of dates) {
		const ms = dayUtcMs(iso);
		if (ms === null) return false;
		if (prev !== null && ms - prev !== 86_400_000) return false;
		prev = ms;
	}
	return true;
}

/**
 * Wire params for the single-request plan, or `null` when the span cannot be
 * asked for in one request.
 *
 * @param {string[]} dates Ordered ISO days of the span.
 * @returns {{date: string, days: number}|null}
 */
export function singleRequestParams(dates) {
	return isContiguousSpan(dates) ? { date: dates[0], days: dates.length } : null;
}

/**
 * Does the server's `range` cover every day of the span in `tz`?
 *
 * @param {{from?: number, to?: number}|null|undefined} range
 * @param {string[]} dates Ordered, contiguous ISO days.
 * @param {string} tz Timezone the request was sent with.
 */
export function rangeCoversSpan(range, dates, tz) {
	const from = Number(range?.from);
	const to = Number(range?.to);
	if (!Number.isFinite(from) || !Number.isFinite(to) || !isContiguousSpan(dates)) return false;
	const firstMs = dayUtcMs(dates[0]);
	const lastMs = dayUtcMs(dates[dates.length - 1]);
	const wantFrom = fromDisplayMs(firstMs, tz);
	const wantTo = fromDisplayMs(lastMs + 86_400_000, tz);
	if (wantFrom === null || wantTo === null) return false;
	return from <= wantFrom / 1000 && to >= wantTo / 1000;
}
